import React, {useState} from "react";
import {NavLink} from "react-router-dom";
import ContentBox from "../../../../../../components/content-box/ContentBox";
import PageActions from "../../../../../base/PageActions";
import Breadcrumbs from "../../../../../../components/breadcrumbs/Breadcrumbs";
import Modal from "../../../../../../components/modal/Modal";
import _image_service from "../../../../../../services/ImageService";
import '../../../../../styles/Common.scss';
import '../../WorldPages.scss';
import '../../WorldDynamicContent.scss';

interface ReemergenceHistoryPageProps {
}

const ReemergenceHistoryPage = (props: ReemergenceHistoryPageProps) => {

    const _map_src = `chase/world/geography/casarok_post_cataclysm.png`;

    const page: PageActions = new PageActions({
        title: `Casarok History: Reemergence Age - Chase the Fox`,
        description: "The Reemergence Age of Casarok is the time in which new kingdoms rose after society recovered from the Great Cataclysm!",
        keywords: ["casarok", "casarok history", "reemergence age", "fantasy timeline", "fantasy world history", "medieval fantasy history"],
        imgSrc: _map_src,
        url: `world/history/reemergence-age`
    });

    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

    return page.generateMetaContent(
        <>
            <Modal isOpen={modalIsOpen}
                   onClose={() => setModalIsOpen(false)}>
                <div className="geography-modal-view">
                    <p className="center">
                        <a href={_image_service.getCdnImageSrc(_map_src, 'full')}
                           target="_blank"
                           rel="noopener noreferrer">
                            Click here to view the full size image.
                        </a>
                    </p>
                    <img src={_image_service.getCdnImageSrc(_map_src, 'desktop')}
                         title='Casarok Post-Cataclysm Map'
                         alt="A map of Casarok after the Great Cataclysm"/>
                </div>
            </Modal>
            <ContentBox header="The Reemergence Age of Casarok">
                <Breadcrumbs path={page.getUrl()}/>
                <div className="world-page-static">
                    <a id="visible-element"
                       className="switch-visible geography-modal-link"
                       onClick={() => setModalIsOpen(true)}>
                        <img className="block width-50-percent"
                             src={_image_service.getCdnImageSrc(`chase/world/geography/casarok_post_cataclysm.png`)}
                             title='Casarok Post-Cataclysm Map'
                             alt="A map of Casarok after the Great Cataclysm"/>
                    </a>
                </div>
                <div className="world-page-static">
                    <p className="center">
                        <em>This age covers the years 500 - 1000 ACE.</em>
                    </p>
                    <p>
                        The Reemergence Age is the time after the new settlements of the previous age reformed into new
                        kingdoms. It marks a return to form for the anthros of Casarok after the events of
                        the <NavLink className="world-link" to='/world/history/cataclysmic-age'>Great Cataclysm</NavLink>. New
                        systems of government were established all over the world, mostly echoing the feudal system
                        that would normally have been lost in pre-Cataclysmic times. The parliamentary system set up
                        by <NavLink className="world-link" to="/characters/pyrrhe">Pyrrhe Drake</NavLink> had already
                        been thrown out by those seeking control and power.
                    </p>
                    <p>
                        This age marks the first time in which there was no known contact with
                        the <NavLink className="world-link" to="/world/religion/pantheon">Casarok pantheon</NavLink>. The
                        fate of the planet had truly been left to the anthros. As such, any existing texts from before
                        the Cataclysm are thought of as myth. New religions that had their roots form in the previous
                        age started to become prominent here. While religious entities were not commonly in charge of
                        nations like they were in
                        the <NavLink className="world-link" to='/world/history/feudal-age'>the Feudal Age</NavLink>, some
                        kingdoms did see churches in positions of power. As for science and magic, they started to form
                        equal footing here. Technology had much faster development than the magical arts, and scholars
                        knew it was a trend which would continue onward into the future.
                    </p>
                    <p>
                        <em>How does Casarok's Reemergence Age differ from the medieval times of Earth?</em><br/>
                        Populations had a stable growth from the previous age as there were no devastating pandemics.
                        The Great Cataclysm had already culled numbers. New religions were forming on Casarok as opposed
                        to the already established and powerful ones that were on Earth during the similar time period.
                        Magic also played a part in the continued rebuilding of Casarok, and mage towers continued to
                        be repaired and re-established.
                    </p>
                    <h3 className="headline">Notable Events</h3>
                    <ul className="spaced">
                        <li>
                            <em>512 ACE</em>: Mage towers and castles are rebuilt or newly constructed all around the
                            world.
                        </li>
                        <li>
                            <em>579 ACE</em>: <NavLink className="world-link" to='/world/religion'>Madarism</NavLink> is
                            formally established as a religion on the continent of Centralia.
                        </li>
                        <li>
                            <em>646 ACE</em>: The printing press is invented, allowing for knowledge to be easier spread
                            and historical records to be better maintained so it is not lost like before the Great
                            Cataclysm.
                        </li>
                        <li>
                            <em>685 ACE</em>: Gunpowder begins to be further refined in he Medesian kingdom of Euraline,
                            leading to its early use in warfare through the use of rocket arrows.
                        </li>
                        <li>
                            <em>720 ACE</em>: Cults of Endrianism pop up in pockets around the world, seeking to bring
                            back the Endtimes.
                        </li>
                        <li>
                            <em>742 ACE</em>: Gunpowder spreads to other nations in Centralia due to its land access and
                            trade routes through the mountains with Euraline.
                        </li>
                        <li>
                            <em>771 ACE</em>: The International Council of Magic is founded.
                        </li>
                        <li>
                            <em>829 ACE</em>: Saint Sylvia leads a group of pilgrims into
                            the <NavLink className="world-link" to='/world/geography'>Eldwood</NavLink> to escape
                            religious persecution. They would go on to establish Sylvianism.
                        </li>
                        <li>
                            <em>866 ACE</em>: An Endrianist uprising occurs, threatening the stability of the world yet
                            again.
                        </li>
                        <li>
                            <em>892 ACE</em>: The first primitive hand cannons are invented in Centralia.
                        </li>
                        <li>
                            <em>903 ACE</em>: A kingdom built upon Endrianism attempts to overthrow some of the largest
                            other kingdoms in Centralia.
                        </li>
                        <li>
                            <em>911 ACE</em>: The rogue Endrianist kingdom attempts to push their way into the Valley of
                            Fate to find the source of power for the Endtimes. They are thwarted.
                        </li>
                        <li>
                            <em>935 ACE</em>: The Endrianist kingdom collapses after many years of fighting from other
                            kingdoms and within its own borders.
                        </li>
                        <li>
                            <em>987 ACE</em>: Armies begin to utilize platoons of soldiers which purely employ primitive
                            gunpowder weaponry.
                        </li>
                    </ul>
                </div>
            </ContentBox>
        </>
    );
}

export default ReemergenceHistoryPage;